/**
 * Lazy-loaded FinTech Ops route components with preload support.
 *
 * Preloading on nav-link hover warms the chunk cache so Suspense rarely triggers
 * when the user actually clicks. Each entry exposes `.preload()` returning the
 * import promise (idempotent — React caches the module).
 */
import { ComponentType, lazy } from 'react';

type Loader<T> = () => Promise<{ default: T }>;

interface Preloadable<T extends ComponentType<unknown>> extends React.LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
}

function lazyWithPreload<T extends ComponentType<unknown>>(loader: Loader<T>): Preloadable<T> {
  const Comp = lazy(loader) as Preloadable<T>;
  Comp.preload = loader;
  return Comp;
}

export const LazyRiskRemediatorMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/risk-remediator/RiskRemediator').then((m) => ({ default: m.RiskRemediatorMain as ComponentType<unknown> }))
);
export const LazyUpgradeMedicMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/upgrade-medic/UpgradeMedicPage').then((m) => ({ default: m.UpgradeMedicMain as ComponentType<unknown> }))
);
export const LazyCostMetricsV2Page = lazyWithPreload(() =>
  import('src/components/fintech-ops/cost-metrics-v2/CostMetricsV2Page').then((m) => ({ default: m.CostMetricsV2Page as ComponentType<unknown> }))
);
export const LazyDogmaMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/dogma/DogmaPage').then((m) => ({ default: m.DogmaMain as ComponentType<unknown> }))
);
export const LazyPolicyEnginePage = lazyWithPreload(() =>
  import('src/components/fintech-ops/policy-engine/PolicyEnginePage').then((m) => ({ default: m.PolicyEnginePage as ComponentType<unknown> }))
);
export const LazySasPageMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/sas/SasPage').then((m) => ({ default: m.SasPageMain as ComponentType<unknown> }))
);
export const LazySasV2PageMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/sas-v2/SasV2Page').then((m) => ({ default: m.SasV2PageMain as ComponentType<unknown> }))
);
export const LazyTicketsPageMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/tickets/TicketsPage').then((m) => ({ default: m.TicketsPageMain as ComponentType<unknown> }))
);
export const LazyUserMetricsV2Page = lazyWithPreload(() =>
  import('src/components/fintech-ops/user-metrics-v2/UserMetricsV2Page').then((m) => ({ default: m.UserMetricsV2Page as ComponentType<unknown> }))
);
export const LazyProjectHealthPage = lazyWithPreload(() =>
  import('src/components/fintech-ops/project-health/ProjectHealthPage').then((m) => ({ default: m.ProjectHealthPage as ComponentType<unknown> }))
);
export const LazyPipelineOEDashboard = lazyWithPreload(() =>
  import('src/components/fintech-ops/pipeline-oe-dashboard/PipelineOEDashboard').then((m) => ({
    default: m.PipelineOEDashboard as ComponentType<unknown>
  }))
);
export const LazyOEReportPage = lazyWithPreload(() =>
  import('src/components/fintech-ops/oe-report/OEReportPage').then((m) => ({
    default: m.OEReportPage as ComponentType<unknown>
  }))
);

export const LazyOEReportGenerator = lazyWithPreload(() =>
  import('src/components/fintech-ops/oe-report-generator/OEReportGeneratorPage').then((m) => ({
    default: m.OEReportGeneratorPage as ComponentType<unknown>
  }))
);

export const LazyKingpinPage = lazyWithPreload(() =>
  import('src/components/das-finsuite/kingpin/KingpinPage').then((m) => ({ default: m.KingpinPage as ComponentType<unknown> }))
);

export const LazyCapacityPlannerPage = lazyWithPreload(() =>
  import('src/components/das-finsuite/capacity-planner/CapacityPlannerPage').then((m) => ({
    default: m.CapacityPlannerPage as ComponentType<unknown>
  }))
);

export const LazyHeadcountPage = lazyWithPreload(() =>
  import('src/components/das-finsuite/headcount/HeadcountPage').then((m) => ({
    default: m.HeadcountPage as ComponentType<unknown>
  }))
);

/** Map of route href → preload function. Used by SideNavigation on hover. */
export const ROUTE_PRELOAD_MAP: Record<string, () => Promise<unknown>> = {
  '/operational-excellence/risk-remediator': LazyRiskRemediatorMain.preload,
  '/operational-excellence/upgrade-medic': LazyUpgradeMedicMain.preload,
  '/operational-excellence/cost-metrics': LazyCostMetricsV2Page.preload,
  '/operational-excellence/cost-metrics-v2': LazyCostMetricsV2Page.preload,
  '/operational-excellence/pipeline-health': LazyDogmaMain.preload,
  '/operational-excellence/policy-engine': LazyPolicyEnginePage.preload,
  '/operational-excellence/security-risks': LazySasPageMain.preload,
  '/operational-excellence/security-risks-v2': LazySasV2PageMain.preload,
  '/operational-excellence/ticket-health': LazyTicketsPageMain.preload,
  '/operational-excellence/user-metrics': LazyUserMetricsV2Page.preload,
  '/signal': LazyProjectHealthPage.preload,
  '/operational-excellence/pipeline-oe-dashboard': LazyPipelineOEDashboard.preload,
  '/operational-excellence/oe-report': LazyOEReportPage.preload,
  '/operational-excellence/oe-report-generator': LazyOEReportGenerator.preload,
  '/kingpin': LazyKingpinPage.preload,
  '/kingpin/goals': LazyKingpinPage.preload,
  '/kingpin/insights': LazyKingpinPage.preload,
  '/kingpin/charts': LazyKingpinPage.preload,
  '/capacity-planner': LazyCapacityPlannerPage.preload,
  '/line-up': LazyHeadcountPage.preload
};
